import React, { useState, useEffect, useRef } from 'react';
import {
  LayoutDashboard,
  PlusCircle,
  Settings,
  LogOut,
  Receipt,
  Command,
  Building2,
  ChevronRight,
  ChevronDown,
  Loader2,
} from 'lucide-react';
import { ViewState } from '@/types';
import type { CustomerOrganization } from '@cfleet/api-client';

interface SidebarProps {
  currentView: ViewState;
  setView: (view: ViewState) => void;
  onSignOut: () => void;
  organizations: CustomerOrganization[];
  organizationsLoading?: boolean;
  onNewOrder: (orgId: string) => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  currentView,
  setView,
  onSignOut,
  organizations,
  organizationsLoading = false,
  onNewOrder,
}) => {
  const [showOrgDropdown, setShowOrgDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const activeOrgs = organizations.filter((o) => o.status === 'active');

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowOrgDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navItemClass = (view: ViewState) => `
    group flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200
    ${
      currentView === view
        ? 'bg-zinc-800/50 text-white'
        : 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800/30'
    }
  `;

  return (
    <div className="hidden lg:flex w-[220px] h-screen border-r border-zinc-800 bg-zinc-925 flex-col justify-between shrink-0 z-50">
      <div>
        <div className="p-4 flex items-center gap-2 mb-2">
          <div className="w-6 h-6 bg-blue-600 rounded flex items-center justify-center">
            <Command size={14} className="text-white" />
          </div>
          <span className="font-semibold text-zinc-100 tracking-tight">cfleet client</span>
        </div>

        <div className="px-2 space-y-1 relative" ref={dropdownRef}>
          <button
            onClick={() => setShowOrgDropdown(!showOrgDropdown)}
            className={`w-full mb-1 flex items-center justify-between px-3 py-2 rounded-md transition-all text-sm font-medium border
              ${
                showOrgDropdown
                  ? 'bg-blue-600 text-white border-blue-500 shadow-lg shadow-blue-900/20'
                  : 'bg-blue-600/10 text-blue-400 border-blue-600/20 hover:bg-blue-600/20'
              }
            `}
          >
            <div className="flex items-center gap-2">
              <PlusCircle size={16} />
              <span>New Order</span>
            </div>
            {showOrgDropdown ? (
              <ChevronDown size={14} />
            ) : (
              <ChevronRight size={14} className="opacity-50" />
            )}
          </button>
          {showOrgDropdown && (
            <div className="absolute left-2 right-2 top-11 bg-zinc-900 border border-zinc-800 rounded-lg shadow-2xl z-[60] py-1 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
              <div className="px-3 py-1.5 text-[10px] font-bold text-zinc-500 uppercase tracking-widest border-b border-zinc-800/50 mb-1">
                Select Dispatch Partner
              </div>
              <div className="max-h-48 overflow-y-auto custom-scrollbar dark-scroll">
                {organizationsLoading ? (
                  <div className="px-3 py-4 flex items-center justify-center">
                    <Loader2 size={16} className="animate-spin text-zinc-500" />
                    <span className="ml-2 text-xs text-zinc-500">Loading...</span>
                  </div>
                ) : activeOrgs.length > 0 ? (
                  activeOrgs.map((org) => (
                    <button
                      key={org.id}
                      onClick={() => {
                        onNewOrder(org.id);
                        setShowOrgDropdown(false);
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-xs text-zinc-300 hover:bg-blue-600 hover:text-white transition-colors text-left"
                    >
                      <Building2 size={12} className="opacity-50" />
                      <span className="truncate">{org.name}</span>
                    </button>
                  ))
                ) : (
                  <div className="px-3 py-4 text-center text-[10px] text-zinc-500 italic">
                    No active partners found
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="h-4" /> {/* Spacer */}
          <div className="px-3 pb-2 pt-1 text-xs font-semibold text-zinc-500 uppercase tracking-wider">
            Workspace
          </div>
          <button onClick={() => setView('dashboard')} className={navItemClass('dashboard')}>
            <LayoutDashboard size={18} className="text-zinc-500 group-hover:text-zinc-300" />
            <span>Overview</span>
          </button>
          <button
            onClick={() => setView('organizations')}
            className={navItemClass('organizations')}
          >
            <Building2 size={18} className="text-zinc-500 group-hover:text-zinc-300" />
            <span>Organizations</span>
          </button>
          <button onClick={() => setView('invoices')} className={navItemClass('invoices')}>
            <Receipt size={18} className="text-zinc-500 group-hover:text-zinc-300" />
            <span>Invoices</span>
          </button>
        </div>
      </div>

      <div className="p-2 border-t border-zinc-800">
        <button onClick={() => setView('settings')} className={navItemClass('settings')}>
          <Settings size={18} className="text-zinc-500 group-hover:text-zinc-300" />
          <span>Settings</span>
        </button>
        <button
          onClick={onSignOut}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-zinc-400 hover:text-red-400 hover:bg-red-900/10 transition-all duration-200 mt-1"
        >
          <LogOut size={18} />
          <span>Sign out</span>
        </button>
      </div>
    </div>
  );
};
